import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { userAPI } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';

const VerifyEmailPage: React.FC = () => {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const verificationAttemptedRef = useRef(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');

    console.log('[VERIFY-EMAIL-PAGE] Component mounted:', {
      pathname: location.pathname,
      search: location.search,
      token,
      hasToken: !!token,
      tokenLength: token?.length || 0,
      alreadyAttempted: verificationAttemptedRef.current
    });

    if (verificationAttemptedRef.current) {
      console.log('[VERIFY-EMAIL-PAGE] Verification already attempted, skipping');
      return;
    }

    if (!token) {
      console.log('[VERIFY-EMAIL-PAGE] ERROR: No token in URL');
      setStatus('error');
      setErrorMessage(t.verifyEmail?.noToken || 'No verification token found.');
      verificationAttemptedRef.current = true;
      return;
    }

    const verifyToken = async () => {
      console.log('[VERIFY-EMAIL-PAGE] Starting verification with token:', token);
      verificationAttemptedRef.current = true;
      try {
        console.log('[VERIFY-EMAIL-PAGE] Calling userAPI.verifyEmail...');
        const response = await userAPI.verifyEmail(token);
        console.log('[VERIFY-EMAIL-PAGE] Verification successful:', response);
        setStatus('success');
      } catch (err: any) {
        console.error('[VERIFY-EMAIL-PAGE] Verification failed:', {
          error: err,
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        setStatus('error');
        setErrorMessage(err.response?.data?.error || t.verifyEmail?.genericError || 'An error occurred.');
      }
    };

    verifyToken();
  }, [location.search, location.pathname, t]);

  return (
    <div className="py-12 max-w-2xl mx-auto px-4 text-center">
      <h1 className="text-3xl font-bold text-gray-900 mb-4">
        {t.verifyEmail?.title || 'Email Verification'}
      </h1>
      <div className="bg-white p-8 rounded-lg shadow-md">
        {status === 'verifying' && (
          <p className="text-lg text-gray-600">{t.verifyEmail?.verifying || 'Verifying your email...'}</p>
        )}
        {status === 'success' && (
          <div>
            <h2 className="text-2xl font-bold text-green-600 mb-4">{t.verifyEmail?.success || 'Your email has been successfully updated!'}</h2>
            <button onClick={() => navigate('/account')} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              {t.verifyEmail?.backToAccount || 'Go to My Account'}
            </button>
          </div>
        )}
        {status === 'error' && (
          <div>
            <p className="text-lg text-red-600 mb-4">{t.verifyEmail?.errorTitle || 'Verification Failed'}</p>
            <p className="text-gray-700 bg-red-50 p-3 rounded-md mb-4">{errorMessage}</p>
            <button onClick={() => navigate('/account')} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              {t.verifyEmail?.backToAccount || 'Go to My Account'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
